import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { t } from "i18next";
import { Loader2Icon } from "lucide-react";
import { useState } from "react";
import type { VainuCompany } from "@/types/account/vainu_company";
import AppService from "@/services/AppService";
import useDebounce from "@/hooks/useDebounce";

const appService = new AppService();

interface SearchCompanyInputProps {
	value: string;
	placeholder: string;
	onChange: (value: string) => void;
	onSelect: (value: VainuCompany) => void;
}

export const SearchCompanyInput = ({
	value,
	placeholder,
	onChange,
	onSelect,
}: SearchCompanyInputProps) => {
	const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
	const debouncedValue = useDebounce(value, 200);

	const { data, isSuccess, isLoading } = useQuery({
		queryKey: ["searchCompany", debouncedValue],
		queryFn: () => appService.searchCompany(debouncedValue),
		enabled: debouncedValue?.length > 0,
	});

	return (
		<div className="relative flex items-center justify-start">
			<Input
				onFocus={(e) => {
					setIsAutocompleteOpen(true);
					e.target.select();
				}}
				onBlur={() => {
					setIsAutocompleteOpen(false);
				}}
				className="bg-soft-white dark:bg-black"
				placeholder={placeholder}
				value={value}
				onChange={(e) => {
					onChange(e.target.value);
				}}
			/>

			<div className="absolute">
				<Popover open={isAutocompleteOpen}>
					<PopoverTrigger asChild>
						<div className="absolute py-3 left-0 pointer-events-none" />
					</PopoverTrigger>
					<PopoverContent
						align="start"
						onOpenAutoFocus={(e) => {
							e.preventDefault();
						}}
						className="w-[400px] p-2 min-h-[100px] max-h-[50svh] overflow-auto flex justify-center gap-2"
					>
						{isSuccess && data?.Result.length > 0 && (
							<div className="flex flex-col w-full divide-y">
								{/* Bug on the server, not in react */}
								{data.Result.map((company) => {
									return (
										<Button
											variant="ghost"
											key={company.company_name}
											className="flex h-16 flex-col gap-1 items-start"
											onClick={() => {
												onSelect(company);
											}}
										>
											<p>{company.company_name}</p>
											<p className="text-sm text-gray-500">
												{company.business_id}
											</p>
										</Button>
									);
								})}
							</div>
						)}

						{!isSuccess && !isLoading && !data && (
							<div className="flex items-center justify-center text-sm text-gray-600 dark:text-gray-400">
								<p>{t("admin.filter.start-typing")}</p>
							</div>
						)}
						{isSuccess && data?.Result.length === 0 && (
							<div className="flex items-center justify-center text-sm text-gray-600 dark:text-gray-400">
								<p>{t("admin.filter.no-companies-found")}</p>
							</div>
						)}
						{isLoading && (
							<div className="flex items-center justify-center text-sm text-gray-600 dark:text-gray-400">
								<Loader2Icon className="h-8 w-8 animate-spin text-howden-blue" />
							</div>
						)}
					</PopoverContent>
				</Popover>
			</div>
		</div>
	);
};
